/**
 * IAN Assist — Request/Response Schema (V1)
 *
 * Stable JSON contract between add-in and n8n backend.
 * n8n is STATELESS — every request is self-contained, processed, returned, discarded.
 * No data persisted in n8n. All API keys live server-side.
 *
 * Architecture: Monolithic n8n — all intelligence runs inside
 * a single n8n workflow. The add-in is a thin card renderer.
 */

/**
 * Build a RequestV1 payload.
 */
export function buildRequest({
  tenantId = 'xceedance',
  userId = '',
  userDisplayName = '',
  persona = 'claims-adjuster',
  trigger = 'mail_opened',
  email = {},
  selection = null,
  config = {},
  workflowId = '',
  customDag = null,
}) {
  return {
    version: '1',
    tenantId,
    userId,
    userDisplayName,
    persona,
    trigger,
    workflowId: workflowId || undefined,
    customDag: customDag || undefined,
    email: {
      id: email.id || '',
      conversationId: email.conversationId || '',
      subject: email.subject || '',
      from: email.from || { name: '', email: '' },
      to: email.to || [],
      cc: email.cc || [],
      receivedAt: email.receivedAt || new Date().toISOString(),
      body: {
        text: email.bodyText || '',
        html: email.bodyHtml || '',
      },
      thread: email.thread || [],
      attachments: email.attachments || [],
    },
    selection,
    config: {
      enabledIntents: config.enabledIntents || ['summarize', 'actions', 'reply'],
      summaryStyle: config.summaryStyle || 'executive',
      replyTone: config.replyTone || 'professional',
      targetLanguage: config.targetLanguage || 'en',
      composePrompt: config.composePrompt || '',
      composeTone: config.composeTone || 'professional',
      composeType: config.composeType || 'general',
    },
  };
}

/**
 * Parse a ResponseV1 from n8n.
 */
export function parseResponse(raw) {
  // n8n sometimes returns a stringified JSON string — parse it first
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      throw new Error('Invalid response: could not parse JSON string');
    }
  }

  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid response: expected JSON object');
  }

  // n8n sub-workflows sometimes return [{ card: {...} }] or { card: {...} }.
  // Normalize to a single envelope so downstream cards always render.
  let envelope = raw;
  if (Array.isArray(raw)) {
    const merged = { cards: [] };
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      if (Array.isArray(entry.cards)) merged.cards.push(...entry.cards);
      else if (entry.card && typeof entry.card === 'object') merged.cards.push(entry.card);
      Object.assign(merged, {
        version: merged.version || entry.version,
        intent: merged.intent || entry.intent,
        confidence: merged.confidence ?? entry.confidence,
        telemetry: merged.telemetry || entry.telemetry,
      });
    }
    envelope = merged;
  }

  const rawCards = Array.isArray(envelope.cards)
    ? envelope.cards
    : (envelope.card && typeof envelope.card === 'object')
      ? [envelope.card]
      : [];

  return {
    version: envelope.version || '1',
    intent: envelope.intent || '',
    confidence: typeof envelope.confidence === 'number' ? envelope.confidence : 0,
    cards: rawCards.map(normalizeCard),
    telemetry: {
      latencyMs: envelope.telemetry?.latencyMs || 0,
      tokens: envelope.telemetry?.tokens || 0,
      cached: envelope.telemetry?.cached || false,
    },
  };
}

/**
 * Extract a nested text field from a (possibly truncated) JSON string.
 * Looks for "body_text":"..." or "body":"..." and extracts the value.
 * Handles escaped characters within the JSON string value.
 */
function extractNestedField(jsonStr) {
  // Try body_text first, then body, then body_markdown
  for (const field of ['body_text', 'body', 'body_markdown']) {
    // Match "field":"<value>" — value may contain escaped quotes (\")
    const pattern = new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)`, 's');
    const match = jsonStr.match(pattern);
    if (match && match[1]) {
      // Unescape the JSON string value
      try {
        // Add closing quote to make it valid JSON, then parse
        const unescaped = JSON.parse('"' + match[1] + '"');
        // Only use it if it looks like actual content (not another JSON blob)
        if (unescaped && unescaped.length > 20 && !unescaped.trim().startsWith('{')) {
          return unescaped;
        }
      } catch {
        // Manual unescape fallback
        const manual = match[1]
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
        if (manual.length > 20 && !manual.trim().startsWith('{')) {
          return manual;
        }
      }
    }
  }
  return null;
}

function normalizeCard(card) {
  // If card is a string (e.g. n8n returned JSON as a string), try to parse it
  if (typeof card === 'string') {
    try {
      card = JSON.parse(card);
    } catch {
      // Not valid JSON — treat the string as body_text of a summary card
      return {
        id: `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'summary',
        title: '',
        body_markdown: card,
        body_html: '',
        body_text: card,
        subject: '',
        language: '',
        tone: '',
        items: [],
        actions: [],
      };
    }
  }

  const type = card.type || 'summary';
  const rawItems = Array.isArray(card.items) ? card.items : [];

  // Per-type field remap so server payloads with alternate key names
  // (e.g. n8n action_items emitting action/owner/deadline) render correctly.
  const items = type === 'action_items'
    ? rawItems.map((it, i) => ({
        id: it.id || `item-${i}`,
        text: it.text ?? it.action ?? '',
        assignee: it.assignee ?? it.owner ?? null,
        due: it.due ?? it.deadline ?? null,
        priority: it.priority ?? null,
      }))
    : rawItems;

  // Resolve body_text: n8n may return content in body_text, body, or body_markdown.
  // Also detect when body_text is a stringified JSON dump of the card itself
  // (double-nested card). The inner JSON may also be truncated, so JSON.parse
  // can fail. Use regex extraction as a robust fallback.
  let bodyText = card.body_text || card.body || '';
  let bodyMarkdown = card.body_markdown || '';
  if (bodyText && typeof bodyText === 'string') {
    const trimmedBody = bodyText.trim();
    if (trimmedBody.startsWith('{') || trimmedBody.startsWith('[')) {
      // First try: parse as valid JSON
      try {
        const parsed = JSON.parse(trimmedBody);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          bodyText = parsed.body_text || parsed.body || parsed.body_markdown || parsed.text || bodyText;
          bodyMarkdown = bodyMarkdown || parsed.body_markdown || '';
        }
      } catch {
        // JSON.parse failed — likely truncated. Use regex to extract nested body_text value.
        const extracted = extractNestedField(trimmedBody);
        if (extracted) {
          bodyText = extracted;
        }
      }
    }
  }

  return {
    id: card.id || `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    title: card.title || '',
    body_markdown: bodyMarkdown,
    body_html: card.body_html || '',
    body_text: bodyText,
    subject: card.subject || '',
    language: card.language || '',
    tone: card.tone || '',
    items,
    actions: Array.isArray(card.actions) ? card.actions : [],
  };
}

/**
 * Build a Phase 2 action execution request.
 */
export function buildActionRequest({ actionId, actionText, emailContext = {}, userId = '', tenantId = 'xceedance' }) {
  return {
    version: '1',
    actionId,
    actionText,
    context: {
      emailId: emailContext.id || '',
      subject: emailContext.subject || '',
      from: emailContext.from || '',
    },
    userId,
    tenantId,
  };
}
